import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Menu,
  Search,
  PackageCheck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

export default function PickingListPage() {
  const { authAxios } = useAuth();
  const [pickingList, setPickingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedProduct, setExpandedProduct] = useState(null);

  useEffect(() => {
    const fetchPickingList = async () => {
      try {
        const res = await authAxios.get("/orders/picking-list");
        setPickingList(res.data);
      } catch {
        toast.error("Errore nel caricamento lista di scarico");
      } finally {
        setLoading(false);
      }
    };
    fetchPickingList();
  }, [authAxios]);

  const filtered = pickingList.filter(
    (p) =>
      p.product_name.toLowerCase().includes(search.toLowerCase()) ||
      (p.product_sku || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalProducts = filtered.length;
  const totalQuantity = filtered.reduce((sum, p) => sum + p.total_quantity, 0);

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="picking-list-page">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Lista di Scarico" />
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <PackageCheck className="w-7 h-7 text-emerald-400" />
              Lista di Scarico
            </h1>
            <p className="text-zinc-400 text-sm">Prodotti raggruppati per gli ordini attivi (Ricevuti + In Preparazione)</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="bg-[#18181b] border-white/5">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-500/10">
                <PackageCheck className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono text-emerald-400">{totalProducts}</p>
                <p className="text-xs text-zinc-500">Prodotti</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-white/5">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10">
                <PackageCheck className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono text-amber-400">{totalQuantity.toFixed(1)}</p>
                <p className="text-xs text-zinc-500">Quantità Totale</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="picking-search"
            placeholder="Cerca prodotto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#18181b] border-white/10 text-white placeholder:text-zinc-500"
          />
        </div>

        {/* Picking List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton h-16 rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-[#18181b] border-white/5">
            <CardContent className="p-10 text-center">
              <p className="text-zinc-400">Nessun prodotto da preparare</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-1.5">
            {filtered.map((product) => (
              <Card
                key={product.product_id}
                data-testid={`picking-item-${product.product_id}`}
                className="bg-zinc-800 border-zinc-700 shadow-md shadow-black/40 hover:border-zinc-600 hover:shadow-lg hover:shadow-black/50 hover:-translate-y-px transition-all"
              >
                <CardContent className="p-0">
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 text-left"
                    onClick={() =>
                      setExpandedProduct(
                        expandedProduct === product.product_id ? null : product.product_id
                      )
                    }
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <p className="text-white font-medium text-sm truncate">{product.product_name}</p>
                        {product.product_sku && (
                          <span className="text-zinc-500 text-xs font-mono">{product.product_sku}</span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {product.orders.length} ordini
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-amber-400 font-mono font-bold text-lg">
                          {product.total_quantity}
                        </p>
                        <p className="text-zinc-500 text-xs">{product.unit}</p>
                      </div>
                      {expandedProduct === product.product_id ? (
                        <ChevronUp className="w-4 h-4 text-zinc-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-zinc-500" />
                      )}
                    </div>
                  </button>

                  {expandedProduct === product.product_id && (
                    <div className="border-t border-white/5 px-4 py-3 space-y-2">
                      {product.orders.map((o) => (
                        <div
                          key={`${o.order_id || o.order_numero}-${o.client_name}-${o.quantity}`}
                          className="flex items-center justify-between text-sm py-1"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-amber-400 font-mono text-xs">{o.order_numero}</span>
                            <span className="text-zinc-300">{o.client_name}</span>
                          </div>
                          <span className="text-white font-mono">{o.quantity} {product.unit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
